import { useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../Assets/Images/logo.svg';
import Icon from '../ui/Icon';

/* Shared look for the password-reset steps (Pompi mobile style, adapted to a centred card). */

export const inputBase =
  'w-full h-13 px-5 text-base text-on-surface rounded-2xl border outline-none transition-all placeholder:text-on-surface-variant/50 disabled:opacity-60';
export const inputOk =
  'bg-[#f1f2fc] border-transparent focus:bg-surface-container-lowest focus:border-primary-container focus:ring-2 focus:ring-primary-container/20';
export const inputInvalid = 'bg-status-alert-bg border-error focus:ring-2 focus:ring-error/15';

export const pillButton =
  'w-full h-13 px-6 rounded-full bg-primary-container hover:bg-primary active:scale-[0.99] text-on-primary font-bold text-base flex items-center justify-center gap-2 shadow-sm transition-all outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100';

/** Red message box; `action` renders a button next to the text (e.g. "Try again"). */
export function AlertBox({ children, action, tone = 'alert', id, ref }) {
  const tones = {
    alert: 'border-status-alert-border bg-status-alert-bg text-status-alert-text',
    pending: 'border-status-pending-border bg-status-pending-bg text-status-pending-text',
  };
  return (
    // tabIndex -1 lets callers move focus here when there's nothing actionable to focus
    <div ref={ref} id={id} role="alert" tabIndex={-1} className={`flex items-start gap-2 rounded-xl border px-3 py-2.5 text-xs outline-none ${tones[tone]}`}>
      <Icon name={tone === 'alert' ? 'error' : 'schedule'} size={16} />
      <div className="flex-1">
        <p>{children}</p>
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

/** Password input with a show/hide (eye) toggle. */
export function PasswordInput({ id, invalid, className = '', ...props }) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={shown ? 'text' : 'password'}
        aria-invalid={invalid || undefined}
        className={`${inputBase} pr-12 ${invalid ? inputInvalid : inputOk} ${className}`}
        {...props}
      />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        aria-controls={id}
        title={shown ? 'Hide password' : 'Show password'}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-on-surface-variant hover:text-on-surface hover:bg-surface-container outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
      >
        <Icon name={shown ? 'visibility_off' : 'visibility'} size={22} />
      </button>
    </div>
  );
}

/** Centred card on the login background with the Pompi logo and a "Back to Login" footer. */
export default function AuthShell({ children, footer = true }) {
  return (
    <div className="min-h-screen flex justify-center items-start sm:items-center bg-surface-container-low sm:py-10">
      <main className="w-full sm:max-w-[440px] min-h-screen sm:min-h-0 bg-[#f7f8fd] flex flex-col sm:rounded-[36px] sm:shadow-2xl overflow-hidden">
        <div className="flex-1 flex flex-col px-5 sm:px-7 pt-10 pb-6">
          <div className="flex flex-col items-center mb-4">
            <div className="relative w-52 h-52 flex items-center justify-center -my-6">
              <div className="absolute inset-8 bg-emerald-200/40 blur-2xl rounded-full pointer-events-none" />
              <img src={logo} alt="Pompi" className="relative w-full h-full object-contain" />
            </div>
          </div>

          <section className="w-full bg-surface-container-lowest rounded-[28px] p-6 sm:p-7 shadow-[0_12px_36px_-8px_rgba(18,38,32,0.08),0_4px_16px_-4px_rgba(18,38,32,0.04)] border border-slate-100/80 mb-6">
            {children}
          </section>

          {footer && (
            <footer className="mt-auto pt-2 pb-2 text-center text-[14px] text-on-surface-variant">
              Remember password?{' '}
              <Link
                to="/login"
                className="font-bold text-primary-container underline decoration-2 underline-offset-2 hover:text-primary rounded outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
              >
                Back to Login
              </Link>
            </footer>
          )}
        </div>
      </main>
    </div>
  );
}
